import React from 'react';
import classNames from 'classnames';

import { Condition, getConditionTestId, getRandomGuid } from '../rule/rule-utils';
import GenericRow from './generic-row';
import IndexedLabel from '../../../components/indexed-label/indexed-label';
import AttributePath from '../rule/components/attribute-path';

import './multi-condition.scss';

export interface BaseCondition {
	id: number;
}

export interface RowComponentBaseProps<TConditionType extends BaseCondition> {
	disabled?: boolean;
	singleCondition: TConditionType;
	setIsValid(isValid: boolean): void;
	onChange(condition: TConditionType): void;
}

export interface MultiConditionTranslations {
	addRow: string;
	removeTooltipText?: string;
}

export interface MultiConditionProps<TConditionType extends BaseCondition> {
	disabled?: boolean;
	index?: number;
	maxAttributePathItems?: number;
	condition: Condition;
	translations: MultiConditionTranslations;
	rowClassName?: string;
	withIndexedLabel?: boolean;

	onChange(condition: Condition, value: TConditionType[]): void;
	onRemove(condition: Condition): void;

	children: (props: RowComponentBaseProps<TConditionType>) => React.ReactNode;
}

const getNewCondition = (): BaseCondition => ({
	id: getRandomGuid()
});

const MultiCondition = <TConditionType extends BaseCondition>({
	index,
	condition,
	onChange,
	onRemove,
	translations,
	rowClassName,
	maxAttributePathItems,
	disabled = false,
	withIndexedLabel = true,
	children
}: MultiConditionProps<TConditionType>) => {
	if (typeof children !== 'function') {
		throw new Error('children must be a function');
	}
	const { name, value } = condition;

	const conditions = ((value as TConditionType[]).length ? value : [getNewCondition()]) as TConditionType[];

	const addCondition = () => {
		onChange(condition, [...conditions, getNewCondition() as TConditionType]);
	};

	const removeCondition = conditionId => {
		if (conditions.length === 1) {
			onRemove(condition);
			return;
		}
		onChange(
			condition,
			conditions.filter(cond => cond.id !== conditionId)
		);
	};

	const handleChange = (updatedRowCondition, index) => {
		let newConditions = [...conditions];
		newConditions[index] = updatedRowCondition;
		onChange(condition, newConditions);
	};

	return (
		<div
			className={classNames('condition-container', { 'disabled-condition': disabled })}
			data-test-id={getConditionTestId(name)}
		>
			{index !== undefined && withIndexedLabel && <IndexedLabel index={index} />}
			<div id={!withIndexedLabel ? 'short-name' : undefined} className="name">
				<AttributePath path={name} maxPathItems={maxAttributePathItems} />
			</div>
			<div className="multi-condition-rows-container">
				{conditions.map((value, index) => (
					<GenericRow
						disabled={disabled}
						key={value.id}
						isLast={index === conditions.length - 1}
						onRemove={() => removeCondition(value.id)}
						onAdd={addCondition}
						addTooltipText={translations.addRow}
						removeTooltipText={translations.removeTooltipText}
						className={rowClassName}
					>
						{props =>
							children({
								...props,
								singleCondition: value,
								onChange: value => handleChange(value, index)
							})
						}
					</GenericRow>
				))}
			</div>
		</div>
	);
};

export default MultiCondition;
